import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/contexts/auth-context';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';
import { Building2, Camera, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const FORM_SUBSIDIARY_EDIT_ID = 'form-subsidiary-edit';

export default function FormSubsidiaryEdit({ subsidiary, subsidiaryLogoUrl, onSubmit }) {
    const { t } = useTranslation();
    const { user } = useAuth();

    const fileInputRef = useRef(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        siret: '',
        vat: '',
        description: '',
        notes: '',
        street: '',
        postalCode: '',
        city: '',
        email: '',
        billingEmail: '',
        ...(user.role === 'admin' && { billingId: '' }),
        phone: '',
    });

    useEffect(() => {
        if (subsidiary) {
            console.log(subsidiary);
            
            setFormData({
                name: subsidiary.name || '',
                siret: subsidiary.siret || '',
                vat: subsidiary.vat || '',
                description: subsidiary.description || '',
                notes: subsidiary.notes || '',
                street: subsidiary.street || '',
                postalCode: subsidiary.postalCode || subsidiary.cp || '',
                city: subsidiary.city || '',
                email: subsidiary.email || '',
                billingEmail: subsidiary.billing_email || '',
                phone: subsidiary.phone || '',
                ...(user.role === 'admin' && { billingId: subsidiary.billing_id || '' }),

            });
        }
    }, [subsidiary]);

    useEffect(() => {
        if (subsidiaryLogoUrl) {
            setLogoPreview(subsidiaryLogoUrl);
        }
    }, [subsidiaryLogoUrl]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) return;
        if (file.size > 2 * 1024 * 1024) return;

        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => {
            setLogoPreview(ev.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleLogoRemove = () => {
        setLogoFile(null);
        setLogoPreview(subsidiaryLogoUrl || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;

        const submitData = {
            ...subsidiary,
            ...formData,
        };

        if (logoFile) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const base64 = ev.target.result.split(',')[1];
                submitData.logoBase64 = base64;
                submitData.logoFileName = logoFile.name;
                onSubmit(submitData);
            };
            reader.readAsDataURL(logoFile);
        } else {
            onSubmit(submitData);
        }
    };

    return (
        <form
            id={FORM_SUBSIDIARY_EDIT_ID}
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-6 py-4 space-y-5"
        >
            {/* Logo upload */}
            <div>
                <label className="mb-[8px] block">
                    <span className="text-[14px] text-foreground">{t('subsidiary:logo')}</span>
                </label>
                <div className="flex items-start gap-[16px]">
                    <div className="h-[80px] w-[80px] rounded-[12px] bg-surface border-2 border-dashed border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                        {logoPreview ? (
                            <img src={logoPreview} alt={t('subsidiary:logoPreview')} className="w-full h-full object-cover" />
                        ) : (
                            <Building2 className="h-8 w-8 text-muted-foreground" />
                        )}
                    </div>
                    <div className="flex-1">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            className="hidden"
                            onChange={handleLogoSelect}
                        />
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-[8px] px-[16px] py-[10px] bg-foreground rounded-[10px] hover:bg-foreground/90 transition-colors"
                            >
                                <Camera className="h-4 w-4 text-background" />
                                <span className="text-[13px] text-background">
                                    {logoPreview ? t('subsidiary:changerLeLogo') : t('subsidiary:ajouterUnLogo')}
                                </span>
                            </button>
                            {logoFile && (
                                <button
                                    type="button"
                                    onClick={handleLogoRemove}
                                    className="flex items-center justify-center h-[40px] w-[40px] rounded-[10px] border border-border hover:bg-surface-hover transition-colors"
                                >
                                    <X className="h-4 w-4 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-[8px]">
                            {t('subsidiary:formatRecommandePngOuJpg200x200pxMinimum')}
                        </p>
                    </div>
                </div>
            </div>

            <Field>
                <FieldLabel required>{t('subsidiary:nomDuSousCompte')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('subsidiary:monSousCompteSas')}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                />
            </Field>

            <Field>
                <FieldLabel>SIRET</FieldLabel>
                <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="123 456 789 00012"
                    value={formData.siret}
                    onChange={(e) => handleChange('siret', e.target.value.replace(/\D/g, '').slice(0, 14))}
                    maxLength={14}
                />
            </Field>

            <Field>
                <FieldLabel>{t('subsidiary:nTvaIntracommunautaire')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('subsidiary:fr00000000000')}
                    maxLength={20}
                    value={formData.vat}
                    onChange={(e) => handleChange('vat', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('subsidiary:description')}</FieldLabel>
                <Textarea
                    placeholder={t('subsidiary:descriptionDuSousCompte')}
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('subsidiary:notes')}</FieldLabel>
                <Textarea
                    placeholder={t('subsidiary:notesInternes')}
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('subsidiary:adresse')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('subsidiary:25RueDeLaLiberte')}
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                />
            </Field>

            <div className="grid grid-cols-2 gap-2">
                <Field>
                    <FieldLabel>{t('subsidiary:codePostal')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder="13001"
                        value={formData.postalCode}
                        onChange={(e) => handleChange('postalCode', e.target.value)}
                    />
                </Field>
                <Field>
                    <FieldLabel>{t('subsidiary:ville')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('subsidiary:marseille')}
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                    />
                </Field>
            </div>

            <Field>
                <FieldLabel>{t('subsidiary:emailDeContact')}</FieldLabel>
                <Input
                    type="email"
                    placeholder="contact@sous-compte.fr"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                />
            </Field>

            {/* ID de facturation */}
            {
                user.role === 'admin' && (
                    <Field>
                        <FieldLabel required>
                            {t('subsidiary:identifiantDeFacturation')}
                        </FieldLabel>
                        <Input
                            type="text"
                            placeholder={t('subsidiary:ex123')}
                            value={formData.billingId}
                            onChange={(e) => handleChange('billingId', e.target.value)}
                            required
                        />
                    </Field>
                )
            }

            <Field>
                <FieldLabel>{t('subsidiary:emailDeFacturation')}</FieldLabel>
                <Input
                    type="email"
                    placeholder="facturation@sous-compte.fr"
                    value={formData.billingEmail}
                    onChange={(e) => handleChange('billingEmail', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('subsidiary:telephone')}</FieldLabel>
                <Input
                    type="tel"
                    placeholder="+33 4 91 00 00 00"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                />
            </Field>
        </form>
    );
}
